import { useEffect, useState } from "react";
import { Plus, Printer, FileText, Trash2, Loader2, ChevronDown } from "lucide-react";
import { roleMap } from "@/lib/api";
import RoleProfile from "@/components/rolemap/RoleProfile";
import RoleAsk from "@/components/rolemap/RoleAsk";

const TOKEN_KEY = "sn_admin_token";

function TokenGate({ onToken }) {
  const [tok, setTok] = useState("");
  return (
    <form data-testid="rolemap-token-form" onSubmit={(e) => { e.preventDefault(); onToken(tok.trim()); }} className="max-w-sm">
      <input data-testid="rolemap-token-input" type="password" value={tok} onChange={(e) => setTok(e.target.value)} placeholder="Admin token" className="w-full h-11 px-3 text-sm border border-[#e5e0d8] rounded-md bg-white" />
      <button data-testid="rolemap-token-submit" className="mt-3 h-10 px-5 rounded-full btn-guide text-sm font-medium">Open</button>
    </form>
  );
}

function NewSession({ token, onCreated }) {
  const [f, setF] = useState({ title: "", company: "", role: "", jd: "" });
  const [open, setOpen] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    const s = await roleMap.create(token, { ...f, title: f.title || `${f.company || "Company"} · ${f.role || "Role"}` });
    setF({ title: "", company: "", role: "", jd: "" });
    setOpen(false);
    onCreated(s);
  };
  return (
    <div className="border border-[#e5e0d8] rounded-lg bg-white">
      <button data-testid="new-session-toggle" onClick={() => setOpen((o) => !o)} className="w-full h-10 px-3 text-xs font-medium inline-flex items-center gap-2 text-[#14352a]">
        <Plus className="h-3.5 w-3.5 text-[#9a7218]" /> New interview <ChevronDown className={`ml-auto h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <form data-testid="new-session-form" onSubmit={create} className="p-3 pt-0 space-y-2">
          <input data-testid="new-company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} placeholder="Company" className="w-full h-9 px-2.5 text-xs border border-[#e5e0d8] rounded-md" />
          <input data-testid="new-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="Role title" className="w-full h-9 px-2.5 text-xs border border-[#e5e0d8] rounded-md" />
          <textarea data-testid="new-jd" value={f.jd} onChange={(e) => setF({ ...f, jd: e.target.value })} placeholder="Paste the job description (optional, lets AI spot gaps vs. the ad)" rows={4} className="w-full px-2.5 py-2 text-xs border border-[#e5e0d8] rounded-md" />
          <button data-testid="new-session-submit" className="h-9 px-4 rounded-full btn-guide text-xs font-medium">Create</button>
        </form>
      )}
    </div>
  );
}

export default function RoleMap() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [ai, setAi] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { document.title = "Role Map, Sinthu Nathan"; }, []);

  const run = async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      return await fn();
    } catch (e) {
      setErr(e.message);
      if (e.message === "Wrong admin token") { sessionStorage.removeItem(TOKEN_KEY); setToken(""); }
    } finally {
      setBusy(false);
    }
  };

  const loadList = (tok) => run(async () => {
    const l = await roleMap.list(tok);
    sessionStorage.setItem(TOKEN_KEY, tok);
    setSessions(l);
    if (l[0]) {
      const first = await roleMap.get(tok, l[0].id);
      setActive((cur) => cur ?? first);
    }
  });

  useEffect(() => { if (token) loadList(token); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSession = (id) => run(async () => setActive(await roleMap.get(token, id)));
  const onCreated = (s) => { setSessions((l) => [s, ...l]); setActive(s); };
  const onAsk = (body) => run(async () => setActive(await roleMap.addEntry(token, active.id, body)));
  const onSuggest = () => run(async () => { const r = await roleMap.suggest(token, active.id); setActive((s) => ({ ...s, ...r })); });
  const onRemoveEntry = (eid) => run(async () => setActive(await roleMap.removeEntry(token, active.id, eid)));
  const onProfile = (profile) => { setActive((s) => ({ ...s, profile })); roleMap.patchProfile(token, active.id, profile).catch(() => {}); };
  const onSummary = () => run(async () => { const r = await roleMap.summary(token, active.id); setActive((s) => ({ ...s, summary: r.summary })); });
  const onDelete = () => run(async () => {
    if (!window.confirm("Delete this interview?")) return;
    await roleMap.remove(token, active.id);
    setSessions((l) => l.filter((s) => s.id !== active.id));
    setActive(null);
  });

  return (
    <div data-testid="rolemap-page" className="min-h-screen bg-[#f2ece4] text-[#14352a]">
      <header className="no-print border-b border-[#e5e0d8] bg-[#faf8f5]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-14 flex items-center gap-4">
          <span className="font-mono-label text-[10px] text-[#9a7218]">Role Map · private discovery tool</span>
          <span className="text-sm font-medium">Sinthu Nathan</span>
          {active && (
            <div className="ml-auto flex items-center gap-2">
              <button data-testid="summary-btn" onClick={onSummary} disabled={busy} className="h-9 px-3 rounded-full border border-[#14352a] text-xs inline-flex items-center gap-1.5 hover:bg-[#14352a] hover:text-white disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} AI summary
              </button>
              <button data-testid="print-btn" onClick={() => window.print()} className="h-9 px-3 rounded-full btn-guide text-xs inline-flex items-center gap-1.5">
                <Printer className="h-3.5 w-3.5" /> Export PDF
              </button>
              <button data-testid="delete-session-btn" onClick={onDelete} className="h-9 w-9 grid place-items-center rounded-full border border-[#e5e0d8] text-[#86928a] hover:text-[#b3261e]" aria-label="Delete interview"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-6">
        {!token ? (
          <TokenGate onToken={setToken} />
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            <aside className="no-print lg:col-span-3 space-y-3">
              <NewSession token={token} onCreated={onCreated} />
              <ul data-testid="session-list" className="space-y-1.5">
                {sessions.map((s) => (
                  <li key={s.id}>
                    <button data-testid={`session-${s.id}`} onClick={() => openSession(s.id)} className={`w-full text-left p-3 rounded-md border text-xs ${active?.id === s.id ? "bg-[#14352a] text-white border-[#14352a]" : "bg-white border-[#e5e0d8] hover:border-[#9a7218]"}`}>
                      <div className="font-medium">{s.title}</div>
                      <div className={`mt-0.5 font-mono-label text-[8px] ${active?.id === s.id ? "text-[#d9a92e]" : "text-[#86928a]"}`}>{(s.updated_at || "").slice(0, 10)}</div>
                    </button>
                  </li>
                ))}
              </ul>
              {err && <p data-testid="rolemap-error" className="text-xs text-[#b3261e]">{err}</p>}
            </aside>

            {active ? (
              <>
                <section className="no-print lg:col-span-5 p-5 bg-[#faf8f5] border border-[#e5e0d8] rounded-lg">
                  <h1 data-testid="session-title" className="font-display text-2xl leading-tight">{active.title}</h1>
                  <p className="text-xs text-[#55625b]">{active.role}{active.company ? ` · ${active.company}` : ""}</p>
                  <div className="mt-5">
                    <RoleAsk session={active} ai={ai} setAi={setAi} busy={busy} onAsk={onAsk} onSuggest={onSuggest} onRemoveEntry={onRemoveEntry} />
                  </div>
                </section>
                <section className="lg:col-span-4 print-sheet">
                  <div className="hidden print:block mb-4 border-b-2 border-[#14352a] pb-2">
                    <div className="font-mono-label text-[9px] text-[#9a7218]">Role Map · {new Date().toLocaleDateString("en-GB")}</div>
                    <div className="font-display text-xl">{active.title}</div>
                  </div>
                  <RoleProfile profile={active.profile || {}} onChange={onProfile} />
                  {active.summary && (
                    <article data-testid="summary" className="mt-4 p-5 bg-[#14352a] text-[#faf8f5] rounded-lg whitespace-pre-line text-sm leading-relaxed print:bg-white print:text-[#14352a] print:border print:border-[#14352a]">
                      <div className="font-mono-label text-[9px] text-[#d9a92e] print:text-[#9a7218] mb-2">AI summary</div>
                      {active.summary}
                    </article>
                  )}
                </section>
              </>
            ) : (
              <p className="lg:col-span-9 text-sm text-[#55625b]">Create an interview on the left to start.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
